import React, { useCallback, useEffect, useRef, useState } from 'react'
import Icon from '../../blocks.simple/icon/icon'
import styles from './ScrollableFilterList-styles'
import { cn } from 'ethcss'

type ScrollDirection = 'next' | 'prev'

interface IScrollableFilterListProps {
    children: React.ReactElement<{ className?: string; children?: React.ReactNode }>
    refreshArrowsTrigger?: unknown
}

const getChildrenLength = (children: IScrollableFilterListProps['children']) =>
    React.Children.toArray(children.props.children).length

export const ScrollableFilterListView: React.FC<IScrollableFilterListProps> = ({ children, refreshArrowsTrigger }) => {
    const [currentIndex, setCurrentIndex] = useState(0)
    const [isAvailableArrows, setIsAvailableArrows] = useState(true)
    const [isRightLimit, setIsRightLimit] = useState(false)
    const filterListRef = useRef<HTMLDivElement>(null)
    const shiftLeftRef = useRef(0)
    const childrenLength = getChildrenLength(children)

    const getListElement = useCallback((): HTMLElement | null => {
        const { className } = children.props

        if (!className) return null

        const element = document.getElementsByClassName(className).item(0)
        return element instanceof HTMLElement ? element : null
    }, [children])

    const slideReset = () => {
        const list = getListElement()

        if (!list) return

        list.style.transform = `translateX(0)`
        setCurrentIndex(0)
    }

    const slideList = useCallback(
        (direction: ScrollDirection) => {
            const list = getListElement()

            if (!list) return

            const isNext = direction === 'next'
            const items = list.children
            const defaultStep = 1
            const actionStep = isNext
                ? currentIndex + defaultStep < items.length - 1
                    ? defaultStep
                    : items.length - 1 - currentIndex
                : currentIndex - defaultStep > 0
                ? defaultStep
                : 0
            const itemIndex = isNext
                ? currentIndex + actionStep
                : currentIndex - actionStep === currentIndex
                ? 0
                : currentIndex - actionStep
            const item = items.item(itemIndex)

            if (!(item instanceof HTMLElement)) return

            const moveValue = -item.offsetLeft

            setIsRightLimit(item.offsetLeft > list.offsetWidth)

            list.style.transform = `translateX(${moveValue + shiftLeftRef.current}px)`

            setCurrentIndex(
                isNext
                    ? currentIndex + actionStep
                    : currentIndex - actionStep === currentIndex
                    ? 0
                    : currentIndex - actionStep
            )
            shiftLeftRef.current = 0
        },
        [currentIndex, getListElement]
    )

    const checkAvailableArrows = useCallback(() => {
        const list = getListElement()

        if (list) {
            const itemsSum = Array.from(list.children).reduce((sum, item) => {
                return item instanceof HTMLElement ? sum + item.offsetWidth : sum
            }, 0)

            setIsAvailableArrows(itemsSum + 60 > (filterListRef.current?.offsetWidth ?? 0))
        }
    }, [getListElement])

    useEffect(() => {
        slideReset()
        checkAvailableArrows()

        window.addEventListener('resize', checkAvailableArrows)

        return () => window.removeEventListener('resize', checkAvailableArrows)
    }, [childrenLength, refreshArrowsTrigger, checkAvailableArrows])

    return (
        <div
            ref={filterListRef}
            className={styles.scrollableFilterList}
            style={{ padding: isAvailableArrows ? '0 30px' : '0' }}
        >
            {isAvailableArrows ? (
                <Icon
                    containerClass={cn(styles.arrow, styles.arrowLeft)}
                    onClick={() => slideList('prev')}
                    type={'left_icon_menu'}
                    size={14}
                    disabled={currentIndex === 0}
                />
            ) : null}
            <div className={styles.scrollArea}>{children}</div>
            {isAvailableArrows ? (
                <Icon
                    containerClass={cn(styles.arrow, styles.arrowRight)}
                    onClick={() => slideList('next')}
                    type={'right_icon_menu'}
                    size={14}
                    disabled={currentIndex >= getChildrenLength(children) - 1 || isRightLimit}
                />
            ) : null}
        </div>
    )
}
